import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Building,
  Activity,
  History,
  AlertTriangle,
  FileText,
  BarChart2,
  Link,
  ChevronLeft,
  ChevronUp,
  CreditCard,
  HelpCircle,
  LogOut,
} from "lucide-react";

const DashboardSidebar = ({
  isMobile,
  isCollapsed,
  setIsCollapsed,
  isHovering,
  setIsHovering,
  isMenuOpen,
  setIsMenuOpen,
  activeSection,
  setActiveSection,
  themeClasses,
  isDarkMode,
  hasCompanyManagement,
  hasFeature,
  hasInProgressTasks,
  showStatusDot,
  hasUnviewedCompletedTasks,
  lastCompletionStatus,
  hasPendingReviewDocs,
  fetchDocumentHistory,
  username,
  showAccountDropdown,
  setShowAccountDropdown,
  accountDropdownRef,
  favicon,
  lightlogo,
  darklogo,
  handleLogout,
}) => {
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isMobile, isMenuOpen, setIsMenuOpen]);
  const getSections = () => {
    let baseSections = [
      { id: "scan", icon: "Camera", color: "blue" },
      { id: "status", icon: "Activity", color: "blue" },
      { id: "history", icon: "History", color: "blue" },
      { id: "templates", icon: "FileText", color: "blue" },
      { id: "usage", icon: "BarChart2", color: "blue" },
      { id: "integrations", icon: "Link", color: "blue" },
    ];

    let companyManagedSections = hasCompanyManagement()
      ? [
          { id: "scan", icon: "Camera", color: "blue" },
          { id: "companies", icon: "Building", color: "blue" },
          { id: "status", icon: "Activity", color: "blue" },
          { id: "history", icon: "History", color: "blue" },
          { id: "templates", icon: "FileText", color: "blue" },
          { id: "usage", icon: "BarChart2", color: "blue" },
          { id: "integrations", icon: "Link", color: "blue" },
        ]
      : baseSections;

    // Add review section only if user has the review feature
    if (hasFeature("review")) {
      const reviewSection = {
        id: "review",
        icon: "AlertTriangle",
        color: "blue",
      };

      // Insert after "history" for base sections
      const insertAfterHistory = (sections) => {
        const idx = sections.findIndex((sec) => sec.id === "history");
        if (idx !== -1) {
          const newSections = [...sections];
          newSections.splice(idx + 1, 0, reviewSection);
          return newSections;
        }
        return sections;
      };

      baseSections = insertAfterHistory(baseSections);
      companyManagedSections = insertAfterHistory(companyManagedSections);
    }

    return hasCompanyManagement() ? companyManagedSections : baseSections;
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case "Camera":
        return <Camera size={18} />;
      case "Building":
        return <Building size={18} />;
      case "Activity":
        return (
          <>
            <Activity size={18} />
            {activeSection !== "status" &&
              (hasInProgressTasks || showStatusDot) && (
                <div
                  className={`
                    absolute top-1 -right-1 w-3 h-3 rounded-full 
                    border border-white shadow-lg animate-bounce bg-amber-500
                  `}
                />
              )}
          </>
        );
      case "History":
        return (
          <>
            <History size={18} />
            {hasUnviewedCompletedTasks && (
              <div
                className={`
                  absolute top-1 -right-1 w-3 h-3 rounded-full 
                  border border-white shadow-lg animate-bounce
                  ${
                    lastCompletionStatus === "success"
                      ? "bg-emerald-500"
                      : lastCompletionStatus === "failure"
                      ? "bg-red-500"
                      : lastCompletionStatus === "mixed"
                      ? "bg-amber-500"
                      : "bg-blue-500"
                  }
                `}
              />
            )}
          </>
        );
      case "AlertTriangle":
        return (
          <>
            <AlertTriangle size={18} />
            {hasPendingReviewDocs && (
              <div
                className={`
                  absolute top-1 -right-1 w-3 h-3 rounded-full 
                  border border-white shadow-lg animate-bounce bg-red-500
                `}
              />
            )}
          </>
        );
      case "FileText":
        return <FileText size={18} />;
      case "BarChart2":
        return <BarChart2 size={18} />;
      case "Link":
        return <Link size={18} />;
      default:
        return null;
    }
  };

  const sections = getSections();
  const colorVariants = {
    blue: isDarkMode
      ? "from-blue-500/20 to-blue-600/20 border-blue-500/30"
      : "from-blue-50 to-blue-100/50 border-blue-200/50",
    green: isDarkMode
      ? "from-emerald-500/20 to-emerald-600/20 border-emerald-500/30"
      : "from-emerald-50 to-emerald-100/50 border-emerald-200/50",
  };

  return (
    <motion.div
      ref={sidebarRef}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      initial={false}
      animate={{
        // Desktop: normal collapse/expand behavior
        // Mobile: full width when open, completely hidden when closed
        width: isMobile
          ? isMenuOpen
            ? 270
            : 0 // Mobile: 270px when open, 0 when closed
          : isCollapsed
          ? 110
          : 300, // Desktop: 110px collapsed, 300px expanded

        // Mobile: slide in/out from left
        // Desktop: always visible (no x transform)
        x: isMobile
          ? isMenuOpen
            ? 0
            : -270 // Mobile: slide from left
          : 0, // Desktop: always at x=0

        opacity: isMobile
          ? isMenuOpen
            ? 1
            : 0 // Mobile: fade in/out
          : 1, // Desktop: always visible
      }}
      transition={{
        duration: 0.05,
        ease: "easeInOut",
      }}
      className={`
        ${isMobile ? "fixed" : "relative"}
        ${isMobile ? "top-0" : "top-0"} // Account for mobile header height
        inset-y-0 left-0 
        ${themeClasses.leftPanel}
        p-5 shadow-2xl
        overflow-hidden
        rounded-2xl
        m-3
        flex flex-col  
        ${isMobile ? "z-50" : "z-auto"}
        bg-opacity-90
        hover:bg-opacity-95
        ${isMobile ? "h-auto" : "h-auto"} // Full height minus header on mobile
      `}
    >
      {/* Logo and Collapse/Expand Toggle Button */}
      <div
        className={`flex items-center mb-8 ${
          // On mobile, always show expanded layout (no collapsed state)
          // On desktop, show collapsed or expanded based on isCollapsed
          isMobile
            ? "justify-between"
            : isCollapsed
            ? "justify-center relative" // Added relative for absolute positioning
            : "justify-between"
        }`}
      >
        {/**Logo - Always show on mobile, show/hide based on collapse on desktop */}
        {!isCollapsed && (
          <AnimatePresence mode="wait">
            <motion.div
              key={isDarkMode ? "dark" : "light"}
              onClick={() => setActiveSection("scan")}
              className="flex items-center justify-center h-12 w-auto cursor-pointer"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: isMobile ? 1 : 1,
                scale: isMobile ? 1 : 1,
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
            >
              <img
                src={isDarkMode ? darklogo : lightlogo}
                alt="ClevrScan"
                className="w-auto filter brightness-110 object-contain"
                style={{
                  height: isDarkMode ? "48px" : "38px",
                  maxHeight: "100%",
                  maxWidth: "100%",
                }}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {/**Favicon - Never show on mobile, show when collapsed on desktop, hide on hover when collapsed */}
        {isCollapsed && !isMobile && (
          <motion.img
            src={favicon}
            alt="ClevrScan"
            animate={{
              opacity: isHovering ? 0 : 1,
              scale: isHovering ? 0.8 : 1,
            }}
            transition={{ duration: 0.2 }}
            className="h-9 w-9 filter brightness-110 object-contain"
          />
        )}

        {/* Collapse/Expand Toggle Button - Show when not collapsed OR when collapsed and hovering */}
        {!isMobile && (!isCollapsed || (isCollapsed && isHovering)) && (
          <motion.button
            onClick={() => setIsCollapsed(!isCollapsed)}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{ duration: 0.2 }}
            className={`
              border backdrop-blur-sm w-9 h-9 rounded-full
              flex items-center justify-center z-10
              transition-all duration-300 shadow-lg
              hover:scale-110
              ${
                isCollapsed && isHovering
                  ? "absolute inset-0 m-auto" // Center it absolutely when collapsed and hovering
                  : "ml-auto relative" // Normal positioning when expanded
              }
              ${
                isDarkMode
                  ? "bg-slate-700 hover:bg-slate-600 text-white border-slate-600"
                  : "bg-white hover:bg-gray-50 text-gray-600 border-gray-200"
              }
            `}
          >
            <ChevronLeft
              size={20}
              className={`transition-transform duration-300 ${
                isCollapsed ? "rotate-180" : ""
              }`}
            />
          </motion.button>
        )}
      </div>

      {/* Navigation Menu - Add flex-1 to take up available space */}
      <nav className="flex-1">
        <motion.div
          animate={{
            // On mobile, always use expanded padding
            padding: isMobile ? "12px" : isCollapsed ? "10px" : "12px",
          }}
          transition={{ duration: 0.2 }}
          className={`
            rounded-2xl backdrop-blur-sm border
            ${
              isDarkMode
                ? "bg-slate-800/30 border-slate-700/30"
                : "bg-white border-blue-500/30"
            }
          `}
        >
          <ul className="space-y-1">
            {sections.map((section, index) => (
              <li
                key={section.id}
                style={{ animationDelay: `${index * 50}ms` }}
                className="animate-fadeInUp"
              >
                <motion.button
                  onClick={() => {
                    setActiveSection(section.id);
                    // Always close mobile menu when clicking a nav item
                    if (isMobile) {
                      setIsMenuOpen(false);
                    }
                    if (section.id === "history") {
                      fetchDocumentHistory();
                    }
                  }}
                  animate={{
                    // On mobile, always use expanded layout
                    padding: isMobile
                      ? "8px 12px"
                      : isCollapsed
                      ? "8px"
                      : "8px 12px",
                    justifyContent: isMobile
                      ? "flex-start"
                      : isCollapsed
                      ? "center"
                      : "flex-start",
                  }}
                  transition={{ duration: 0.2 }}
                  className={`
                    w-full rounded-2xl group
                    flex items-center relative overflow-hidden
                    transition-all duration-500 font-semibold text-sm
                    transform hover:scale-[1.02] active:scale-[0.98]
                    border-indigo-500/30 border-2
                    ${
                      activeSection === section.id
                        ? `bg-gradient-to-r ${
                            colorVariants[section.color]
                          } border shadow-xl ${
                            isDarkMode
                              ? `text-${section.color}-300`
                              : `text-${section.color}-700`
                          }`
                        : isDarkMode
                        ? "text-gray-400 hover:text-white hover:bg-gradient-to-r hover:from-slate-700/40 hover:to-slate-600/40 border border-transparent hover:border-slate-500/30 hover:shadow-lg"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-100/60 hover:to-gray-50/60 border border-transparent hover:border-gray-300/40 hover:shadow-lg"
                    }
                  `}
                >
                  {/* Animated Background Shimmer */}
                  <div
                    className={`
                      absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100
                      bg-gradient-to-r from-transparent via-white/5 to-transparent
                      transform -translate-x-full group-hover:translate-x-full
                      transition-all duration-1000 ease-in-out
                    `}
                  />

                  {/* Icon Container */}
                  <motion.div
                    animate={{
                      // On mobile, always show margin for text
                      marginRight: isMobile ? 16 : isCollapsed ? 0 : 16,
                      width: isMobile ? 32 : isCollapsed ? 32 : 32,
                      height: isMobile ? 32 : isCollapsed ? 32 : 32,
                    }}
                    transition={{ duration: 0.2 }}
                    className={`
                      relative flex items-center justify-center rounded-xl
                      transition-all duration-500 backdrop-blur-sm
                      ${
                        activeSection === section.id
                          ? `bg-gradient-to-br ${
                              colorVariants[section.color]
                            } shadow-lg scale-110`
                          : isDarkMode
                          ? "bg-slate-700/40 group-hover:bg-slate-600/50 group-hover:scale-110"
                          : "bg-gray-200/40 group-hover:bg-gray-300/50 group-hover:scale-110"
                      }
                    `}
                  >
                    {getIcon(section.icon)}
                  </motion.div>

                  {/* Label with Animation - Always show on mobile */}
                  <AnimatePresence>
                    {(isMobile || !isCollapsed) && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="flex-1 text-left"
                      >
                        <div className="font-semibold text-sm leading-tight">
                          {section.id.charAt(0).toUpperCase() +
                            section.id.slice(1)}
                        </div>
                        <div
                          className={`
                            text-xs opacity-70 mt-0.5 transition-opacity duration-300
                            ${
                              activeSection === section.id
                                ? "opacity-90"
                                : "group-hover:opacity-100"
                            }
                          `}
                        >
                          {section.label}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Hover Chevron - Always show when expanded or on mobile */}
                  <AnimatePresence>
                    {(isMobile || !isCollapsed) && (
                      <motion.div
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 0, x: 8 }}
                        whileHover={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 8 }}
                        transition={{ duration: 0.3 }}
                        className={`
                          ml-2 transform
                          ${isDarkMode ? "text-gray-400" : "text-gray-500"}
                        `}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </li>
            ))}
          </ul>
        </motion.div>
      </nav>

      {/* Account Dropdown Section - Always expanded on mobile - NOW AT BOTTOM */}
      <AnimatePresence>
        {(isMobile || !isCollapsed) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative mt-4" // Changed from mt-18 to mt-4
            ref={accountDropdownRef}
          >
            <button
              onClick={() => setShowAccountDropdown(!showAccountDropdown)}
              className={`w-full px-4 py-3 rounded-xl border-2 shadow-sm hover:shadow-lg flex items-center justify-between transition-all duration-300 transform hover:-translate-y-0.5 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700 hover:border-gray-600 backdrop-blur-sm"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center mr-3 font-bold text-sm shadow-md">
                  {username.charAt(0).toUpperCase()}
                </div>
                <div className="text-left">
                  <div
                    className={`font-semibold ${
                      isDarkMode ? "text-white" : "text-slate-800"
                    }`}
                  >
                    {username}
                  </div>
                  <div
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-slate-500"
                    }`}
                  >
                    Account Settings
                  </div>
                </div>
              </div>
              <ChevronUp
                className={`transition-transform duration-300 ${
                  isDarkMode ? "text-gray-400" : "text-slate-400"
                } ${showAccountDropdown ? "rotate-180" : ""}`}
                size={18}
              />
            </button>

            {/* Enhanced Account Dropdown Menu */}
            <AnimatePresence>
              {showAccountDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className={`absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-2xl border overflow-hidden backdrop-blur-sm ${
                    isDarkMode
                      ? "bg-gray-800/95 border-gray-700"
                      : "bg-white/95 border-slate-300"
                  }`}
                >
                  <div className="p-2">
                    {/* Billing Option */}
                    <button
                      onClick={() => {
                        setShowAccountDropdown(false);
                        setActiveSection("billing");
                      }}
                      className={`w-full px-3 py-3 text-left flex items-center rounded-lg transition-all duration-200 group ${
                        isDarkMode
                          ? "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                          : "hover:bg-slate-50 text-slate-700 hover:text-slate-900"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg mr-3 transition-all duration-300 group-hover:scale-110 ${
                          isDarkMode
                            ? "bg-blue-500/20 group-hover:bg-blue-500/30"
                            : "bg-blue-100 group-hover:bg-blue-200"
                        }`}
                      >
                        <CreditCard
                          className={`${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                          size={15}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Billing & Plans
                        </div>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-all duration-300 group-hover:translate-x-1 ${
                          isDarkMode ? "text-gray-500" : "text-slate-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {/* API Docs Option */}
                    <button
                      onClick={() => {
                        setShowAccountDropdown(false);
                        window.open('https://docs.clevrscan.com/', '_blank');
                      }}
                      className={`w-full px-3 py-3 text-left flex items-center rounded-lg transition-all duration-200 group ${
                        isDarkMode
                          ? "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                          : "hover:bg-slate-50 text-slate-700 hover:text-slate-900"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg mr-3 transition-all duration-300 group-hover:scale-110 ${
                          isDarkMode
                            ? "bg-blue-500/20 group-hover:bg-blue-500/30"
                            : "bg-blue-100 group-hover:bg-blue-200"
                        }`}
                      >
                        <FileText
                          className={`${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                          size={15}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">API Docs</div>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-all duration-300 group-hover:translate-x-1 ${
                          isDarkMode ? "text-gray-500" : "text-slate-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>

                    {/* Support Option */}
                    <button
                      onClick={() => setActiveSection("support")}
                      className={`w-full px-3 py-3 text-left flex items-center rounded-lg transition-all duration-200 group ${
                        isDarkMode
                          ? "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                          : "hover:bg-slate-50 text-slate-700 hover:text-slate-900"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg mr-3 transition-all duration-300 group-hover:scale-110 ${
                          isDarkMode
                            ? "bg-blue-500/20 group-hover:bg-blue-500/30"
                            : "bg-blue-100 group-hover:bg-blue-200"
                        }`}
                      >
                        <HelpCircle
                          className={`${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                          size={15}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">Need Help?</div>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-all duration-300 group-hover:translate-x-1 ${
                          isDarkMode ? "text-gray-500" : "text-slate-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {/* Logout Option */}
                    <button
                      onClick={() => {
                        setShowAccountDropdown(false);
                        handleLogout();
                      }}
                      className={`w-full px-3 py-3 text-left flex items-center rounded-lg transition-all duration-200 group ${
                        isDarkMode
                          ? "hover:bg-red-500/10 text-red-400 hover:text-red-300"
                          : "hover:bg-red-50 text-red-600 hover:text-red-700"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg mr-3 transition-all duration-300 group-hover:scale-110 ${
                          isDarkMode
                            ? "bg-red-500/20 group-hover:bg-red-500/30"
                            : "bg-red-100 group-hover:bg-red-200"
                        }`}
                      >
                        <LogOut
                          className={`${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                          size={15}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">Sign Out</div>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-all duration-300 group-hover:translate-x-1 ${
                          isDarkMode ? "text-red-500" : "text-red-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Collapsed Account Section - Only show on desktop - STAYS AT BOTTOM */}
      <AnimatePresence>
        {isCollapsed && !isMobile && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="relative mt-4 flex justify-center group" // Changed from mt-4 to ensure it's at bottom
          >
            <div className="relative">
              {/* Theme indicator ring */}
              <div
                className={`absolute -inset-1 rounded-xl transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gradient-to-r from-blue-500/30 to-indigo-600/30 blur-sm opacity-75"
                    : "bg-gradient-to-r from-amber-300/40 to-orange-400/40 blur-sm opacity-60"
                }`}
              ></div>

              <button
                onClick={() => setIsCollapsed(false)}
                className={`relative w-12 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode ? "ring-2 ring-blue-400/30" : ""
                }`}
              >
                {username.charAt(0).toUpperCase()}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DashboardSidebar;
